import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Bookmark, Calendar, Gauge, Fuel, Settings, Star, Eye, ChevronLeft, ChevronRight, Car } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '@/contexts/AppDataContext';
import { useLayout6Theme } from './theme';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';
import './styles.css';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version: string;
  price: number;
  photos: string[];
  mileage: number;
  fuel: string;
  manufactureYear: number;
  modelYear: number;
  color: string;
  transmission: string;
  isZeroKm: boolean;
  store?: string;
  type?: number;
  statistics?: {
    views?: number;
    favorites?: number;
  };
  optionals?: string[];
}

interface VehicleCardProps {
  vehicle: Vehicle;
  whatsappUrl?: string; // Made optional since we'll use vehicle's store
}

export const VehicleCard = ({ vehicle, whatsappUrl }: VehicleCardProps) => {
  const { config } = useAppData();
  const { secondaryColor } = useLayout6Theme();
  const { availableStores, selectedStore } = useStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [vehicleSlug, setVehicleSlug] = useState<string>(() => {
    const brand = (vehicle.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    const model = (vehicle.model || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
  });
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);

  const storeId = vehicle.store;
  const vehicleStore = useMemo(() => {
    if (!config?.stores) return null;
    return storeId
      ? config.stores.find((s: any) => s._id === storeId) 
      : (selectedStore || config.stores[0]);
  }, [config?.stores, storeId, selectedStore]);
  const showStoreBadge = availableStores.length > 1 && vehicleStore && storeId;

  // Check if vehicle is favorited on mount and generate proper slug - non-blocking
  useEffect(() => {
    // Run async operations without blocking render
    const init = async () => {
      try {
        // Check favorites - non-blocking
        secureGetItem('favorites').then(favorites => {
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(vehicle.id));
          }
        }).catch(() => {}); // Silently fail
        
        // Generate proper slug - non-blocking
        generateVehicleSlug(vehicle).then(slug => {
          setVehicleSlug(slug);
        }).catch(() => {}); // Silently fail - keep temporary slug
      } catch (error) {
        // Silently fail - card will still render with temporary slug
      }
    };
    init();
  }, [vehicle.id]);

  // Handle favorite toggle
  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation when clicking on favorite button
    e.stopPropagation();

    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      // Remove from favorites
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      // Add to favorites
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
    }

    await secureSetItem('favorites', updatedFavorites);

    // Dispatch custom event to update other components
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const nextImage = () => {
    if (isImageLoading) return; // Block if loading
    setIsImageLoading(true);
    setCurrentImageIndex((prev) =>
      prev === vehicle.photos.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (isImageLoading) return; // Block if loading
    setIsImageLoading(true);
    setCurrentImageIndex((prev) =>
      prev === 0 ? vehicle.photos.length - 1 : prev - 1
    );
  };

  // Handle image load complete
  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageLoaded(true);
  };

  // Preload first image immediately - run synchronously without blocking
  useEffect(() => {
    if (vehicle.photos && vehicle.photos.length > 0 && vehicle.photos[0]) {
      const firstImageUrl = vehicle.photos[0];
      
      // Force immediate load - create image object synchronously
      const img = new Image();
      img.src = firstImageUrl;
      
      // Also add preload link for browser optimization
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = firstImageUrl;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      
      // Clean up link after image loads or after timeout
      const cleanup = () => {
        setTimeout(() => {
          if (link.parentNode) {
            link.remove();
          }
        }, 2000);
      };
      
      if (img.complete) {
        cleanup();
      } else {
        img.onload = cleanup;
        img.onerror = cleanup;
      }
    }
  }, [vehicle.photos]);

  // Set loading state when image index changes
  useEffect(() => {
    if (vehicle.photos && vehicle.photos[currentImageIndex] && currentImageIndex !== 0) {
      setIsImageLoading(true);
      setImageLoaded(false);
    }
  }, [currentImageIndex, vehicle.photos]);

  const handleWhatsApp = () => {
    const message = `Olá! Tenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version}\nAno: ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de mais informações.`;
    // Use vehicle's store whatsapp URL, fallback to prop if provided
    const storeWhatsappUrl = vehicleStore?.whatsapp?.ios || whatsappUrl;
    if (storeWhatsappUrl) {
      const url = `${storeWhatsappUrl}&text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
  };

  const handleImageNavigation = (e: React.MouseEvent, direction: 'prev' | 'next') => {
    e.preventDefault();
    e.stopPropagation();
    if (direction === 'prev') {
      prevImage();
    } else {
      nextImage();
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Block navigation if image is loading
    if (isImageLoading) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Link
      to={`/estoque/${vehicleSlug}`}
      className="block h-full wl6-vehicle-card group"
      onClick={handleCardClick}
    >
      <div className="wl6-vehicle-media">
        {isImageLoading && !imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#e8edf3] z-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--wl6-border)] border-t-[var(--wl6-accent)]" />
          </div>
        )}

        {vehicle.photos?.[currentImageIndex] ? (
          (() => {
            const imageUrl = vehicle.photos[currentImageIndex];
            const optimized = generateResponsiveImageUrls(imageUrl, 340, 212);
            const loadingStrategy = getImageLoadingStrategy(currentImageIndex === 0, currentImageIndex === 0);
            return (
              <img
                src={optimized.src}
                srcSet={optimized.srcSet}
                sizes={optimized.sizes || '(max-width: 640px) 90vw, 320px'}
                alt={`${vehicle.brand} ${vehicle.model}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                loading={loadingStrategy.loading}
                fetchpriority={loadingStrategy.fetchpriority}
                decoding="async"
                width={optimized.width}
                height={optimized.height}
                onLoad={handleImageLoad}
                onError={(e) => { e.currentTarget.style.display = 'none'; setIsImageLoading(false); setImageLoaded(true); }}
              />
            );
          })()
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--wl6-muted)]">
            <Car className="h-10 w-10 opacity-40" />
          </div>
        )}

        {vehicle.photos && vehicle.photos.length > 1 && (
          <>
            <button onClick={(e) => handleImageNavigation(e, 'prev')} disabled={isImageLoading} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/95 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10" aria-label="Foto anterior">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={(e) => handleImageNavigation(e, 'next')} disabled={isImageLoading} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/95 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10" aria-label="Próxima foto">
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        )}

        <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1">
          {vehicle.isZeroKm && (
            <Badge className="bg-emerald-600 text-white text-[10px] font-bold border-0">Zero KM</Badge>
          )}
          {showStoreBadge && (
            <Badge variant="secondary" className="text-[10px] border-0 text-white max-w-[120px] truncate" style={{ background: secondaryColor }}>
              {vehicleStore.company}
            </Badge>
          )}
        </div>

        <button onClick={toggleFavorite} className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/95 shadow-sm flex items-center justify-center z-10" title={isFavorited ? 'Remover dos favoritos' : 'Favoritar'}>
          <Bookmark
            className={`h-4 w-4${isFavorited ? ' text-[var(--wl6-button)]' : ' text-[var(--wl6-text-muted)]'}`}
            fill={isFavorited ? 'currentColor' : 'none'}
          />
        </button>
      </div>

      <div className="wl6-vehicle-body">
        <div>
          <p className="wl6-vehicle-price">{formatPrice(vehicle.price)}</p>
          <h3 className="font-semibold mt-1 leading-snug">
            {vehicle.brand} {vehicle.model}
          </h3>
          <p className="text-sm text-[var(--wl6-muted)] line-clamp-1 mt-0.5">{vehicle.version}</p>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--wl6-muted)] pt-2 border-t border-[var(--wl6-border)]">
          {(vehicle.manufactureYear || vehicle.modelYear) && (
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{vehicle.manufactureYear}/{vehicle.modelYear}</span>
          )}
          {vehicle.mileage !== undefined && vehicle.mileage !== null && (
            <span className="flex items-center gap-1"><Gauge className="h-3.5 w-3.5" />{formatMileage(vehicle.mileage)}</span>
          )}
          {vehicle.fuel && <span className="flex items-center gap-1"><Fuel className="h-3.5 w-3.5" />{vehicle.fuel}</span>}
        </div>
      </div>
    </Link>
  );
};